import { Collapse, Grid, useMediaQuery } from '@mui/material'
import { DateRange } from '@mui/x-date-pickers-pro'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { useSelector } from 'react-redux'
import {useNavigate, useLocation } from 'react-router-dom'
import { RequestFieldsModal, requestFieldsDefaultState } from '../../models'
import { setActiveRoute } from '../../redux/reducers/appSettingsReducer'
import { showErrorMessage } from '../../redux/reducers/snackbarReducer'
import { RootStore, useAppDispatch } from '../../redux/store'
import { SelectOption } from '../../services/dashboardService'
import { myReportingService } from '../../services/myReportingService'
import AutocompleteList from '../../shared/UI/AutocompleteList'
import Buttons from '../../shared/UI/Button'
import DateRangePicker from '../../shared/UI/DateRangePicker'
import FilterButton from '../../shared/UI/FilterButton'
import LoadingIndicator from '../../shared/UI/LoadingIndicator'
import RefreshButton from '../../shared/UI/RefreshButton'
import theme from '../../theme/theme'
import { BaseResponse } from '../../types/base-response'
import { getFilteredDepartmentsDropdownOptions } from '../../utils/app-utils'
import UserErrorMessage, { handleSearchError } from '../../utils/errorFilter'
import SicknessSummarySearchGrid from '../SicknessSummarySearchGrid/index'
import { SicknessSummarySearchItem } from '../../types/sickness-summary-search'
import DownloadButton from '../../shared/UI/DownloadButton'
import { formatDateOrDefault } from '../../utils/date-utils'
import { PaginationInfo } from '../Search'

interface ApiError {
  response: {
    data: BaseResponse
  }
}

export interface ReportingSummarySicknessFilters {
  selectedDepartment: SelectOption | null
  selectedEmployee: SelectOption | null
  selectedTeam: SelectOption | null
  dateRange: DateRange<Date>
  employeeList: SelectOption[]
  localData: SicknessSummarySearchItem[] | null
  pagination: PaginationInfo | null
  localDataExpiration: Date | null
}

const headers: string[] = [
  'EmployeeId',
  'EmployeeName',
  'Department',
  'Team',
  'Number of Absences',
  'Hours',
]

const fileDownloadName = 'SicknessSummaryReport.csv'

/**
 * SicknessSummary component displays a filterable sickness summary report
 * with date, department, team, and employee filters and CSV export functionality.
 * 
 * @returns React component for sickness summary reporting
 */
function SicknessSummary() {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [fieldsTouched, setFieldsTouched] = useState<RequestFieldsModal>(requestFieldsDefaultState)
  const { departments, allEmployees, allTeams, directReports, currentEntitlementPeriodResponse } =
    useSelector((state: RootStore) => state.appSettings)

  const [selectedDepartment, setSelectedDepartment] = useState<SelectOption | null>({
    displayValue: 'All',
    value: 0,
  })
  const [selectedEmployee, setSelectedEmployee] = useState<SelectOption | null>({
    displayValue: 'All',
    value: 0,
  })
  const [selectedTeam, setSelectedTeam] = useState<SelectOption | null>({
    displayValue: 'All',
    value: 0,
  })
  const [teamList, setTeamList] = useState<SelectOption[]>([])
  const [showTeamsDropDown, setShowTeamsDropDown] = useState<boolean>(false)

  const startDate = currentEntitlementPeriodResponse?.startDate ?? new Date()
  const endDate = currentEntitlementPeriodResponse?.endDate ?? new Date()

  const [employeeList, setEmployeeList] = useState<SelectOption[]>([])
  const [dateRange, setDateRange] = useState<DateRange<Date>>([startDate, endDate])
  const [localData, setLocalData] = useState<SicknessSummarySearchItem[]>([])
  const [hasLocalData, setHasLocalData] = useState<boolean>(false)
  const [showFilters, setShowFilters] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showGrid, setShowGrid] = useState<boolean>(false)
  const [previousRecordPagination, setPreviousRecordPagination] = useState<PaginationInfo | null>()

  const isLocalDataExpired = (previousState: ReportingSummarySicknessFilters): boolean => {
    if (!previousState.localData || previousState.localData.length === 0) {
      return true
    }

    if (previousState.localDataExpiration && previousState.localDataExpiration < new Date()) {
      return true
    }

    return false
  }

  useEffect(() => {
    const previousState = location.state as ReportingSummarySicknessFilters
    if (!previousState) {
      return
    }
    setSelectedDepartment(previousState.selectedDepartment)
    setSelectedEmployee(previousState.selectedEmployee)
    setSelectedTeam(previousState.selectedTeam)
    setEmployeeList(previousState.employeeList)
    setDateRange(previousState.dateRange)
    if (isLocalDataExpired(previousState)) {
      navigate(location.pathname, { replace: true, state: undefined })
      return
    }
    setShowFilters(false)
    setShowGrid(true)
    if (previousState.localData) {
      setLocalData(previousState.localData)
    }
    setPreviousRecordPagination(previousState.pagination)
    navigate(location.pathname, { replace: true, state: undefined })
  }, [location.state])

  const onHandleDepartmentChange = (event: React.SyntheticEvent, department: SelectOption | null) => {
    if (!department) {
      return
    }
    setSelectedDepartment(department)
    setSelectedTeam({ displayValue: 'All', value: 0 })
    setSelectedEmployee({ displayValue: 'All', value: 0 })
  }

  const onHandleEmployeeChange = (event: React.SyntheticEvent, employee: SelectOption | null) => {
    if (!employee) {
      return
    }
    setSelectedEmployee(employee)
  }

  const onHandleTeamChange = (event: React.SyntheticEvent, team: SelectOption | null) => {
    if (!team) {
      return
    }
    setSelectedTeam(team)
    setSelectedEmployee({ displayValue: 'All', value: 0 })
  }

  const onHandleSearch = () => {
    setIsLoading(true)
    setShowGrid(true)

    const startDateValue = formatDateOrDefault(
      dateRange[0],
      new Date(currentEntitlementPeriodResponse?.startDate ?? new Date())
    )

    const endDateValue = formatDateOrDefault(
      dateRange[1],
      new Date(currentEntitlementPeriodResponse?.endDate ?? new Date())
    )

    myReportingService
      .getSicknessSummaryReports(
        startDateValue,
        endDateValue,
        Number(selectedEmployee!.value),
        Number(selectedDepartment?.value),
        Number(selectedTeam?.value)
      )
      .then(res => {
        setHasLocalData(true)
        setLocalData(res.sicknessSummaryItems)
        setShowFilters(false)
        setIsLoading(false)
      })
      .catch(err => {
        setIsLoading(false)
        handleSearchError(err, dispatch)
      })
  }

  const onHandleReset = () => {
    setShowGrid(false)
    setHasLocalData(false)
    setLocalData([])
    setIsLoading(false)
    setShowFilters(true)
    setSelectedDepartment({ displayValue: 'All', value: 0 })
    setSelectedEmployee({ displayValue: 'All', value: 0 })
    setSelectedTeam({ displayValue: 'All', value: 0 })
    setDateRange([startDate, endDate])
  }

  const onHandleFilters = () => {
    setShowFilters(!showFilters)
  }

  const departmentsList = useMemo(
    () => getFilteredDepartmentsDropdownOptions(departments, allEmployees),
    [allEmployees, departments]
  )

  useEffect(() => {
    let selectedEmployees = directReports

    if (selectedDepartment?.value !== 0) {
      selectedEmployees = directReports.filter(
        emp => emp.departmentId === selectedDepartment?.value
      )
      setShowTeamsDropDown(false)
    } else {
      setShowTeamsDropDown(true)
    }

    if (selectedTeam?.value !== 0) {
      selectedEmployees = selectedEmployees.filter(
        emp => emp.teamId === selectedTeam?.value
      )
    }

    const selectAllItem: SelectOption = { displayValue: 'All', value: 0 }

    let list: SelectOption[] = selectedEmployees.map(item => ({
      displayValue: item.employeeName,
      value: item.employeeId,
    }))

    list = [selectAllItem, ...list]

    setEmployeeList(list)

    let selectedTeams = allTeams

    if (selectedDepartment?.value !== 0) {
      selectedTeams = allTeams.filter(emp => emp.departmentId === selectedDepartment?.value)
    }

    list = selectedTeams.map(item => ({
      displayValue: item.teamName,
      value: item.teamId,
    }))

    setTeamList([selectAllItem, ...list])

    dispatch(setActiveRoute('/reporting'))
  }, [
    selectedDepartment,
    selectedTeam,
    currentEntitlementPeriodResponse,
    allEmployees,
    allTeams,
    directReports
  ])

  const mobile = useMediaQuery(theme.breakpoints.down('sm'))

  const getFiltersState = useCallback(
    (): ReportingSummarySicknessFilters | null =>
      dateRange.length === 2 && dateRange[0] && dateRange[1]
        ? ({
          selectedDepartment,
          selectedEmployee,
          selectedTeam,
          employeeList,
          dateRange: [new Date(dateRange[0]), new Date(dateRange[1])],
          localData,
        } as ReportingSummarySicknessFilters)
        : null,
    [
      dateRange,
      employeeList,
      localData,
      selectedDepartment,
      selectedEmployee,
      selectedTeam,
    ]
  )


  /**
   * Generates a CSV file from sickness summary data
   * 
   * @param filename - The name of the CSV file to download
   */
  const makeCsv = useCallback(
    async (filename: string) => {
      const data: SicknessSummarySearchItem[] = localData

      if (!data || data.length === 0) {
        dispatch(showErrorMessage(<UserErrorMessage name="NoDataToExport" />))
        return
      }

      const csvData: SicknessSummarySearchItem[] = data.map(row => ({
        employeeId: row.employeeId,
        displayName: row.displayName,
        departmentName: row.departmentName,
        teamName: row.teamName,
        occurrences: row.occurrences,
        hours: row.hours,
      }))

      const keys: string[] = Object.keys(csvData[0])

      const csvContent: string = csvData
        .map((row: any) =>
          keys
            .map(k => {
              let cell = row[k] === null || row[k] === undefined ? '' : row[k]

              cell =
                cell instanceof Date ? cell.toLocaleString(navigator.language) : cell.toString().replace(/"/g, '""')

              if (cell.search(/("|,|\n)/g) >= 0) {
                cell = `"${cell}"`
              }
              return cell
            })
            .join(',')
        )
        .join('\n')

      const csvHeaders: string = headers.join(',')

      const blob = new Blob([`${csvHeaders}\n`, csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    [localData]
  )

  const handleDownloadClick = useCallback(
    () => {
      makeCsv(fileDownloadName)
    },
    [makeCsv]
  )

  return (
    <Grid container spacing={4} component="form" noValidate>
      <Grid item xs={12} display="flex" justifyContent="flex-end" pb={2}>
        <FilterButton
          title="Change Filters"
          onClick={onHandleFilters}
          dataTestId="Reporting-ChangeFilters"
        />
        {(localData.length > 0) && (
          <DownloadButton onClick={handleDownloadClick} title="Download Report" />
        )}
        <RefreshButton onClick={onHandleReset} dataTestId="Reporting-RestartBtn" />
      </Grid>

      <Collapse in={showFilters} timeout="auto" unmountOnExit style={{ width: '100%' }}>
        <Grid container spacing={4} pl={4}>
          <Grid item xs={12} lg={2.4}>
            <AutocompleteList
              id="department"
              label="Department"
              data={departmentsList}
              textField="displayValue"
              value={selectedDepartment}
              onChange={onHandleDepartmentChange}
              dataTestId="Reporting-DepartmentDDL"
            />
          </Grid>
          <Grid item xs={12} lg={2.4}>
            <AutocompleteList
              id="team"
              label="Teams"
              data={teamList}
              textField="displayValue"
              value={selectedTeam}
              onChange={onHandleTeamChange}
              disabled={showTeamsDropDown}
              dataTestId="ReportingSearch-TeamsDDL"
            />
          </Grid>
          <Grid item xs={12} lg={2.4}>
            <AutocompleteList
              id="employee"
              label="Employee"
              data={employeeList}
              textField="displayValue"
              value={selectedEmployee}
              onChange={onHandleEmployeeChange}
              dataTestId="Reporting-EmployeeDDL"
            />
          </Grid>
          <Grid item xs={12} lg={4.8}>
            <DateRangePicker
              value={dateRange}
              onChange={newValue => {
                setFieldsTouched({ ...fieldsTouched, date: true })
                setDateRange(newValue)
              }}
              arrange="Horizontal"
              dataTestId="Reporting"
            />
          </Grid>
          <Grid item xs={12} pt={0} mt={0} display="flex" justifyContent="flex-end">
            <Buttons label="Search" loading={isLoading} onClick={onHandleSearch} dataTestId="Reporting-SearchBtn" />
          </Grid>
        </Grid>
      </Collapse>

      <Grid item xs={12}>
        <LoadingIndicator show={isLoading} />
        {!isLoading && showGrid && (
          <SicknessSummarySearchGrid
            rows={localData}
            filtersState={getFiltersState()}
            pagination={previousRecordPagination}
          />
        )}
      </Grid>
    </Grid>
  )
}

export default SicknessSummary

